import React, { Fragment } from 'react';
import theme from '@airasia-common/libraries/theming/theme';

import Row from '../Container/Row';
import Column from '../Container/Column';
import Wrapper from '../Wrapper';
import Divider from '../Divider';

import Styled from '../../utils/Styled';
import useWindowSize from '../../utils/windowSize';
import CardThumbnail from './CardThumbnail';
import Card from './Card';
import {
  cardContentPropTypes,
  listContentCardPropTypes,
  singleContentCardPropTypes,
  templateCardPropTypes,
  templateRateCardPropTypes,
} from './TemplateCard.propTypes';

const CardHeader = Styled(Row, {
  padding: [4, 0],
  paddingTop: [4, 3],
  paddingBottom: 0,
});

const ContentWrapper = Styled(Row, {
  paddingLeft: 4,
  paddingRight: [4, 3],
});

const ContentBottomWrapper = Styled(Row, {
  paddingBottom: [4, 0],
  paddingLeft: 4,
  paddingRight: [4, 3],
});

const ContentLeftColumn = Styled(Wrapper, {
  width: ['50%', 1 / 2],
});

const ContentRightColumn = Styled(Column, {
  padding: 0,
  width: 1 / 2,
  alignItems: 'flex-end',
  textAlign: 'right',
});

const ContentBottomColumn = Styled(Row, {
  width: ['100%'],
});

export const TemplateCard = ({
  thumbnailStyle = {},
  thumbnailImage,
  images,
  onLoadThumbnailImage,
  leftPillText,
  leftPillType,
  rightPillText,
  headerComponent,
  footerComponent,
  focusable,
  children,
  highlight,
  isShowCarousel,
  isHotel,
  imageCount,
  onRoomImageClickHandler,
  override,
  isRecommendedHotel,
}) => {
  const { isMobileView } = useWindowSize();
  const highlightBorder = {
    border: `1px solid ${theme.colors.secondary}`,
    borderRadius: '0 0 4px 4px',
    borderTop: 0,
  };
  const cardStyles = highlight && !isMobileView ? highlightBorder : {};
  const contentWrapperStyles = highlight && isMobileView ? highlightBorder : {};
  const cardThumbnailStyle =
    highlight && isMobileView
      ? {
          borderRadius: 0,
          ...thumbnailStyle,
        }
      : thumbnailStyle;
  return (
    <Card
      padding={isHotel ? 0 : [0, 4]}
      display={override ? 'block' : ['block', 'flex']}
      zIndex={0}
      focusable={focusable}
      {...cardStyles}
    >
      <CardThumbnail
        highlight={highlight}
        image={thumbnailImage}
        images={images}
        onLoadImage={onLoadThumbnailImage}
        leftPillText={leftPillText}
        leftPillType={leftPillType}
        rightPillText={rightPillText}
        thumbnailStyle={cardThumbnailStyle}
        isShowCarousel={isShowCarousel}
        isHotel={isHotel}
        imageCount={imageCount}
        onRoomImageClickHandler={onRoomImageClickHandler}
        override={override}
      />
      <Wrapper
        flex={1}
        marginLeft={[0, 3]}
        padding={
          isRecommendedHotel ? '0px' : isHotel && [0, '16px 16px 16px 0']
        }
        {...contentWrapperStyles}
      >
        {headerComponent && (
          <CardHeader display={override ? 'block' : ['block', 'none']}>
            {headerComponent}
          </CardHeader>
        )}
        {children}
        {footerComponent}
      </Wrapper>
    </Card>
  );
};

TemplateCard.propTypes = templateCardPropTypes;

export const CardContent = ({
  headerComponent,
  leftContentComponent,
  rightContentComponent,
  bottomContentComponent,
  override,
  tripAdvisorData,
  isRecommendedHotel,
  ...restProps
}) => {
  const contentWrapperPaddingBottom =
    bottomContentComponent &&
    bottomContentComponent.props.paymentDiscountType &&
    bottomContentComponent.props.paymentDiscountDescription
      ? 0
      : [4, 0];

  return (
    <>
      <ContentWrapper
        minHeight={'100%'}
        paddingBottom={contentWrapperPaddingBottom}
        {...restProps}
      >
        <ContentLeftColumn
          marginTop={isRecommendedHotel ? '10%' : ''}
          width={override ? '40%' : ['50%', 1 / 2]}
        >
          {headerComponent && (
            <CardHeader display={override ? 'none' : ['none', 'block']}>
              {headerComponent}
            </CardHeader>
          )}
          {leftContentComponent}
        </ContentLeftColumn>
        <ContentRightColumn
          width={override ? '60%' : 1 / 2}
          justifyContent={override ? 'end' : 'flex-end'}
          marginBottom={override && !tripAdvisorData ? '15px' : '0px'}
          marginTop={isRecommendedHotel ? '25%' : ''}
        >
          {rightContentComponent}
        </ContentRightColumn>
      </ContentWrapper>
      {bottomContentComponent &&
        bottomContentComponent.props.paymentDiscountType &&
        bottomContentComponent.props.paymentDiscountDescription && (
          <ContentBottomWrapper>
            <ContentBottomColumn>{bottomContentComponent}</ContentBottomColumn>
          </ContentBottomWrapper>
        )}
    </>
  );
};

CardContent.propTypes = cardContentPropTypes;

export const SingleContentCard = props => {
  const {
    headerComponent,
    leftContentComponent,
    rightContentComponent,
    bottomContentComponent,
    override,
  } = props;

  return (
    <TemplateCard {...props}>
      <CardContent
        headerComponent={headerComponent}
        leftContentComponent={leftContentComponent}
        rightContentComponent={rightContentComponent}
        bottomContentComponent={bottomContentComponent}
        className="room-option"
        override={override}
      />
    </TemplateCard>
  );
};

SingleContentCard.propTypes = singleContentCardPropTypes;

export const TemplateRateCard = ({
  topContentComponent,
  leftContentComponent,
  rightContentComponent,
  bottomContentComponent,
  ...restProps
}) => {
  return (
    <ContentWrapper display="block" minHeight={'100%'} {...restProps}>
      {topContentComponent && <Wrapper>{topContentComponent}</Wrapper>}
      <Row>
        <ContentLeftColumn>{leftContentComponent}</ContentLeftColumn>
        <ContentRightColumn>{rightContentComponent}</ContentRightColumn>
      </Row>
      <Wrapper>{bottomContentComponent}</Wrapper>
    </ContentWrapper>
  );
};

TemplateRateCard.propTypes = templateRateCardPropTypes;

export const ListContentCard = props => {
  const { headerComponent, listContentComponents, separator } = props;

  return (
    <TemplateCard {...props}>
      {headerComponent && (
        <CardHeader display={['none', 'block']} margin={0} marginLeft={4}>
          {headerComponent}
        </CardHeader>
      )}
      <Wrapper>
        {listContentComponents.map((item, index) => {
          const {
            id,
            topContentComponent,
            leftContentComponent,
            rightContentComponent,
            bottomContentComponent,
          } = item;

          return (
            <Fragment key={id}>
              {separator && index !== 0 && (
                <Divider
                  marginTop={[0, 4]}
                  marginBottom={['12px', 4]}
                  marginLeft={4}
                  marginRight={[4, 3]}
                />
              )}
              <TemplateRateCard
                className="room-option"
                id={id}
                topContentComponent={topContentComponent}
                leftContentComponent={leftContentComponent}
                rightContentComponent={rightContentComponent}
                bottomContentComponent={bottomContentComponent}
              />
            </Fragment>
          );
        })}
      </Wrapper>
    </TemplateCard>
  );
};

ListContentCard.propTypes = listContentCardPropTypes;
